import React, { useState, useCallback } from 'react';
import { PageHeader } from '../components/Navigation';
import { apiFetch } from '../utils/api';
import AnafInvoicePreview from '../components/AnafInvoicePreview';
import './AnafExplorerPage.css';

// Format date for display - handle various ANAF date formats
const formatDate = (dateStr) => {
  if (!dateStr) return '-';

  // Log the raw date for debugging
  console.log('📅 formatDate input:', dateStr, 'type:', typeof dateStr);

  try {
    // ANAF can return dates in various formats:
    // - "202512221015" (YYYYMMDDHHmm)
    // - "2025-12-22T10:15:30"
    // - timestamp number

    let date;

    if (typeof dateStr === 'number') {
      // Unix timestamp (milliseconds or seconds)
      date = new Date(dateStr > 9999999999 ? dateStr : dateStr * 1000);
    } else if (typeof dateStr === 'string') {
      // Check for ANAF compact format: YYYYMMDDHHmm or YYYYMMDDHHmmss
      if (/^\d{12,14}$/.test(dateStr)) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const hour = dateStr.substring(8, 10);
        const minute = dateStr.substring(10, 12);
        const second = dateStr.length >= 14 ? dateStr.substring(12, 14) : '00';
        date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
      } else {
        // Standard ISO format or other parseable format
        date = new Date(dateStr);
      }
    }

    // Check if date is valid
    if (!date || isNaN(date.getTime())) {
      console.warn('📅 Invalid date:', dateStr);
      return dateStr; // Return original string if parsing fails
    }

    console.log('📅 Parsed date:', date.toISOString());

    return date.toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    console.error('📅 Date parse error:', e, 'for:', dateStr);
    return dateStr;
  }
};

function AnafExplorerPage() {
  // No breadcrumbs - this is a hidden page accessible only via direct URL

  // Form state
  const [cui, setCui] = useState('');
  const [days, setDays] = useState(30);
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Results state
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchInfo, setSearchInfo] = useState(null); // Store search info for display
  const [companyName, setCompanyName] = useState(null); // Company name from ANAF lookup

  // Invoice preview modal
  const [previewModal, setPreviewModal] = useState({
    open: false,
    loading: false,
    invoice: null,
    error: null,
    messageId: null
  });

  // Search for messages
  const handleSearch = useCallback(async (page = 1) => {
    if (!cui) {
      setError('Introdu un CUI');
      return;
    }

    // Validate days (1-60)
    const validDays = Math.max(1, Math.min(60, parseInt(days) || 30));

    setLoading(true);
    setError(null);
    setHasSearched(true);

    console.log('🔍 Search params:', { cui, days: validDays, useCustomRange, startDate, endDate, page });

    try {
      let url;
      if (useCustomRange && startDate && endDate) {
        url = `/api/anaf/explorer/messages?cui=${cui}&start=${startDate}&end=${endDate}&page=${page}`;
      } else {
        url = `/api/anaf/explorer/messages?cui=${cui}&days=${validDays}`;
      }

      console.log('🔍 Fetching:', url);

      const response = await apiFetch(url);
      const data = await response.json();

      console.log('📦 Response:', data);

      if (data.success) {
        // Log each message for debugging
        if (data.messages) {
          data.messages.forEach((msg, idx) => {
            console.log(`📄 Message [${idx}]:`, msg);
            console.log(`   created_at raw:`, msg.created_at);
            console.log(`   raw data:`, msg.raw);
          });
        }

        setMessages(data.messages || []);
        setPagination(data.pagination || null);
        setSearchInfo({
          cui: cui,
          days: validDays,
          count: data.messages?.length || 0
        });

        // Fetch company name from ANAF
        try {
          const companyResponse = await apiFetch(`/api/anaf/company-lookup?cui=${cui}`);
          const companyData = await companyResponse.json();
          console.log('🏢 Company lookup response:', companyData);
          if (companyData.success && companyData.data?.nume) {
            setCompanyName(companyData.data.nume);
          } else {
            setCompanyName(null);
          }
        } catch (companyErr) {
          console.warn('⚠️ Could not fetch company name:', companyErr);
          setCompanyName(null);
        }
      } else {
        setError(data.error || 'Eroare la cautare');
        setMessages([]);
        setSearchInfo(null);
        setCompanyName(null);
      }
    } catch (err) {
      console.error('❌ Search error:', err);
      setError('Eroare de conexiune: ' + err.message);
      setMessages([]);
      setSearchInfo(null);
      setCompanyName(null);
    } finally {
      setLoading(false);
    }
  }, [cui, days, useCustomRange, startDate, endDate]);

  // View invoice details
  const handleViewInvoice = async (messageId) => {
    setPreviewModal({
      open: true,
      loading: true,
      invoice: null,
      error: null,
      messageId
    });

    try {
      const response = await apiFetch(`/api/anaf/explorer/invoice/${messageId}?cui=${cui}`);
      const data = await response.json();

      if (data.success) {
        setPreviewModal(prev => ({
          ...prev,
          loading: false,
          invoice: data.invoice
        }));
      } else {
        setPreviewModal(prev => ({
          ...prev,
          loading: false,
          error: data.error || 'Eroare la descarcarea facturii'
        }));
      }
    } catch (err) {
      setPreviewModal(prev => ({
        ...prev,
        loading: false,
        error: 'Eroare de conexiune: ' + err.message
      }));
    }
  };

  // Close preview modal
  const closePreviewModal = () => {
    setPreviewModal({
      open: false,
      loading: false,
      invoice: null,
      error: null,
      messageId: null
    });
  };

  return (
    <div className="anaf-explorer-page">
      <PageHeader
        title="ANAF Explorer"
        subtitle="Exploreaza facturile trimise la ANAF"
      />

      {/* Main Content */}
      <div className="explorer-main-content">
        {/* Search Form */}
        <div className="explorer-search-form">
          <div className="search-row">
            {/* CUI Input */}
            <div className="form-group cui-group">
              <label>CUI Emitent</label>
              <input
                type="text"
                value={cui}
                onChange={(e) => setCui(e.target.value)}
                placeholder="Introdu CUI"
                className="cui-input"
              />
            </div>

            {/* Period Selection */}
            <div className="form-group period-group">
              <label>Perioada</label>
              <div className="period-options">
                {!useCustomRange && (
                  <div className="days-input-wrapper">
                    <input
                      type="number"
                      value={days}
                      onChange={(e) => setDays(e.target.value)}
                      min="1"
                      max="60"
                      className="days-input"
                    />
                    <span className="days-label">zile</span>
                  </div>
                )}
                <button
                  type="button"
                  className={`toggle-range-btn ${useCustomRange ? 'active' : ''}`}
                  onClick={() => setUseCustomRange(!useCustomRange)}
                >
                  {useCustomRange ? 'Zile' : 'Interval'}
                </button>
              </div>
            </div>

            {/* Custom Date Range */}
            {useCustomRange && (
              <div className="form-group date-range-group">
                <label>Interval personalizat</label>
                <div className="date-inputs">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="date-input"
                  />
                  <span className="date-separator">-</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="date-input"
                  />
                </div>
              </div>
            )}

            {/* Search Button */}
            <div className="form-group search-btn-group">
              <button
                type="button"
                className="search-btn"
                onClick={() => handleSearch(1)}
                disabled={loading || !cui}
              >
                {loading ? 'Se cauta...' : 'Cauta'}
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="explorer-error">
            <span className="error-icon">!</span>
            {error}
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div className="explorer-loading">
            <div className="loading-spinner"></div>
            <p>Se incarca facturile din ANAF...</p>
          </div>
        )}

        {/* Results */}
        {hasSearched && !loading && (
          <div className="explorer-results">
            <div className="results-header">
              <div className="results-title-row">
                <h3>Rezultate: {messages.length} facturi gasite</h3>
                {searchInfo && (
                  <div className="search-info">
                    {companyName && (
                      <span className="search-info-item company-name">
                        <span className="search-info-label">Firma:</span> {companyName}
                      </span>
                    )}
                    <span className="search-info-item">
                      <span className="search-info-label">CUI:</span> {searchInfo.cui}
                    </span>
                    <span className="search-info-item">
                      <span className="search-info-label">Perioada:</span> ultimele {searchInfo.days} zile
                    </span>
                  </div>
                )}
              </div>
            </div>

            {messages.length === 0 ? (
              <div className="no-results">
                <span className="no-results-icon">O</span>
                <p>Nu s-au gasit facturi pentru criteriile selectate</p>
              </div>
            ) : (
              <div className="results-table-wrapper">
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>ID Mesaj</th>
                      <th>Data Trimitere</th>
                      <th>CUI Beneficiar</th>
                      <th>Status</th>
                      <th>Actiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((msg) => (
                      <tr key={msg.id}>
                        <td className="msg-id">{msg.id}</td>
                        <td className="msg-date">{formatDate(msg.created_at)}</td>
                        <td className="msg-receiver">{msg.receiver_cui || '-'}</td>
                        <td className="msg-status">
                          <span className={`status-badge ${msg.details === 'OK' ? 'status-ok' : 'status-error'}`}>
                            {msg.details}
                          </span>
                        </td>
                        <td className="msg-actions">
                          <button
                            className="view-btn"
                            onClick={() => handleViewInvoice(msg.id)}
                          >
                            Vezi Factura
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="explorer-pagination">
                <button
                  disabled={pagination.current_page <= 1}
                  onClick={() => handleSearch(pagination.current_page - 1)}
                >
                  &lt; Anterior
                </button>
                <span className="page-info">
                  Pagina {pagination.current_page} din {pagination.total_pages}
                </span>
                <button
                  disabled={pagination.current_page >= pagination.total_pages}
                  onClick={() => handleSearch(pagination.current_page + 1)}
                >
                  Urmator &gt;
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invoice Preview Modal */}
      <AnafInvoicePreview
        isOpen={previewModal.open}
        onClose={closePreviewModal}
        invoice={previewModal.invoice}
        loading={previewModal.loading}
        error={previewModal.error}
        messageId={previewModal.messageId}
      />
    </div>
  );
}

export default AnafExplorerPage;
